import { all, put, takeEvery } from "@redux-saga/core/effects";
import {
  ABHANumberCreateABHAAddress,
  acceptAndShareConnectionToClinic,
  checkHealthIdExists,
  createAbhaNumber,
  discoverCareContextUsingClinicId,
  getPatientTokenHistory,
  getPublicHealthPrograms,
  linkAbdmCareContextConfirmOtp,
  linkAbdmCareContextSendOtp,
  searchAbdmClinics,
  searchAbdmClinicsWithHipId,
  shareProfile
} from "../../services/api";
import { throwError } from "../../services/error";
import { isValidArray } from "../../utils/validators";
import { getPatientsCareContexts } from "../patients/actions";
import { setErrorStatus, setSuccessStatus } from "../status/actions";
import store from "../store/store";
import { generateEncryptionValue } from "../../utils/constants";

export const abdmActionTypes = {
  // Clinic Search & Discovery
  SEARCH_ABDM_CLINIC: "SEARCH_ABDM_CLINIC",
  CLEAR_CLINIC_SEARCH_RESULTS: "CLEAR_CLINIC_SEARCH_RESULTS",
  DISCOVER_ABDM_CARE_CONTEXT_WITH_CLINIC_ID:
    "DISCOVER_ABDM_CARE_CONTEXT_WITH_CLINIC_ID",
  GET_SCANNED_CLINIC_DETAILS: "GET_SCANNED_CLINIC_DETAILS",

  // Care Context Linking
  SEND_OTP_TO_LINK_ABDM_CARE_CONTEXT: "SEND_OTP_TO_LINK_ABDM_CARE_CONTEXT",
  CONFIRM_OTP_TO_LINK_ABDM_CARE_CONTEXT:
    "CONFIRM_OTP_TO_LINK_ABDM_CARE_CONTEXT",
  CLEAR_LINK_REF_NUMBER: "CLEAR_LINK_REF_NUMBER",

  // Profile & Sharing
  SHARE_PROFILE: "SHARE_PROFILE",
  SHARE_CONNECTION_TO_CLINIC: "SHARE_CONNECTION_TO_CLINIC",

  // ABHA / Health ID
  CHECK_HEALTH_ID_EXISTS: "CHECK_HEALTH_ID_EXISTS",
  CLEAR_HEALTH_ID_EXISTS_STATUS: "CLEAR_HEALTH_ID_EXISTS_STATUS",
  REGISTER_NEW_ABHA_NUMBER: "REGISTER_NEW_ABHA_NUMBER",
  ABHA_NUMBER_CREATION_REGISTER: "ABHA_NUMBER_CREATION_REGISTER",
  RESET_ABHA_NUMBER_CREATION: "RESET_ABHA_NUMBER_CREATION",

  // Auth / Tokens
  CLEAR_ABDM_AUTH_CREDENTIALS: "CLEAR_ABDM_AUTH_CREDENTIALS",
  GET_ABDM_TRANSACTION_ID: "GET_ABDM_TRANSACTION_ID",
  GET_TOKEN_HISTORY: "GET_TOKEN_HISTORY",

  // Records & History
  CLEAR_FETCHED_RECORDS: "CLEAR_FETCHED_RECORDS",
  SET_SEARCH_HISTORY: "SET_SEARCH_HISTORY",

  // Public Programs
  GET_PUBLIC_HEALTH_PROGRAMS: "GET_PUBLIC_HEALTH_PROGRAMS",

  // Cleanup
  CLEAR_ABDM_CONNECT_DETAILS: "CLEAR_ABDM_CONNECT_DETAILS"
};

function* checkHealthIdExistsWorker(action) {
  try {
    yield setAbdmLoading(true);

    if (action.payload.healthId) {
      const { auth, patients } = store.getState();
      const { currentProfile } = patients;

      const response = yield checkHealthIdExists(
        auth.data.accessToken,
        currentProfile,
        action.payload.healthId
      );
      const { success, data } = response;
      if (success === true) {
        yield setHealthIdExist(data.isHealthIdExists);
      } else {
        yield setHealthIdExist(null);
      }
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* clearFetchedRecordsWorker() {
  try {
    yield setAbdmLoading(true);

    yield put({
      type: "CLEAR_CLINIC_SEARCH_DATA",
      payload: { data: null }
    });

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    setErrorStatus(error);
  }
}

function* setSearchHistoryWorker(action) {
  try {
    yield setAbdmLoading(true);

    yield put({
      type: "PUT_SEARCH_HISTORY",
      payload: {
        data: action.payload.data
      }
    });

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    setErrorStatus(error);
  }
}

function* clearClinicSearchResultsWorker() {
  yield put({
    type: "SET_ABDM_CLINIC_SEARCH_RESULTS",
    payload: {
      searchResults: []
    }
  });
}

function* clearHealthIdExistsStatusWorker(action) {
  try {
    yield setHealthIdExist(null);
  } catch (error) {
    setErrorStatus(error);
  }
}

function* clearAbdmAuthCredentialsWorker() {
  try {
    const { abdm, patients } = store.getState();
    const { currentProfile, accessToken } = patients;

    const hasHealthIds = Array.isArray(abdm?.data?.healthId);
    const noAccessToken =
      !accessToken || accessToken?.[currentProfile]?.token === null;

    if (hasHealthIds && noAccessToken) {
      yield setAbdmHealthIds(null);
    }
  } catch (error) {
    yield setErrorStatus(error);
  }
}

function* searchAbdmClinicWorker(action) {
  try {
    const { abdm, auth, patients } = store.getState();
    const { accessToken, currentProfile } = patients;

    const previousSearchResults = isValidArray(abdm.clinics.search.results)
      ? abdm.clinics.search.results
      : [];

    const alreadySearched = previousSearchResults.some(
      (clinic) => clinic?.identifier?.name === action.payload.searchKey
    );

    if (alreadySearched) {
      return;
    }

    yield setAbdmLoading(true);

    const response = yield searchAbdmClinics(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile,
      action.payload.searchKey
    );

    if (response?.success && response?.data) {
      const clinics = response.data?.reduce((acc, data) => {
        acc[data.identifier.id] = data;
        return acc;
      }, {});

      yield put({
        type: "SET_ABDM_CLINIC_SEARCH_RESULTS",
        payload: { searchResults: clinics }
      });
    } else {
      yield put({
        type: "SET_ABDM_CLINIC_SEARCH_RESULTS",
        payload: { searchResults: [] }
      });
    }
    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    setErrorStatus(error);
  }
}

function* getPublicHealthProgramsWorker() {
  try {
    yield setAbdmLoading(true);

    const { auth, patients } = store.getState();
    const { accessToken, currentProfile } = patients;

    const response = yield getPublicHealthPrograms(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile
    );

    if (response?.success) {
      const programs = response.data.reduce((acc, item) => {
        acc[item?.identifier?.id] = item;
        return acc;
      }, {});

      yield put({
        type: "SET_PUBLIC_HEALTH_PROGRAMS",
        payload: { data: programs }
      });
    } else {
      throwError(response);
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* discoverCareContextWorker(action) {
  try {
    yield setAbdmLoading(true);

    const { auth, patients } = store.getState();
    const { accessToken, currentProfile } = patients;
    const { clinicId, patientId } = action.payload;

    yield put({
      type: "SET_ABDM_SEARCHED_CLINIC_CARE_CONTEXTS",
      payload: {
        [clinicId]: null
      }
    });

    const response = yield discoverCareContextUsingClinicId(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile,
      {
        hipId: clinicId,
        unverifiedIdentifiers: [
          patientId
            ? { type: "MR", value: patientId }
            : {
                type: "MOBILE",
                value: auth.data.phoneNumber
              }
        ]
      }
    );

    if (response?.data) {
      yield put({
        type: "SET_ABDM_SEARCHED_CLINIC_CARE_CONTEXTS",
        payload: {
          [clinicId]:
            response.success === false &&
            response.data?.message?.includes("ABDM Error")
              ? { serverError: true }
              : response.data || {}
        }
      });
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* sendOtpToLinkAbdmCareContextWorker(action) {
  try {
    yield setAbdmOTPLoading(true);

    const { patients, auth, abdm } = store.getState();
    const { clinicId } = action.payload;

    const { accessToken, currentProfile } = patients;

    const { transactionId, referenceNumber, patient } =
      abdm?.clinics?.search?.careContexts?.[clinicId];

    const response = yield linkAbdmCareContextSendOtp(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile,
      {
        transactionId,
        referenceNumber,
        patient,
        hipId: clinicId
      }
    );

    const { data, success } = response;

    if (success === false) {
      setErrorStatus({
        code: "custom",
        message: data?.message
      });
    }

    if (success === true) {
      setSuccessStatus(data?.message);

      yield put({
        type: "SET_ABDM_LINK_REF_NUMBER",
        payload: {
          data: response.data.link.referenceNumber
        }
      });
    }

    yield setAbdmOTPLoading(false);
  } catch (error) {
    yield setAbdmOTPLoading(false);
    yield setErrorStatus(error);
  }
}

function* confirmOtpToLinkAbdmCareContextWorker(action) {
  try {
    yield setAbdmOTPLoading(true);

    const { patients, auth, abdm } = store.getState();
    const { accessToken, currentProfile } = patients;

    const response = yield linkAbdmCareContextConfirmOtp(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile,
      {
        linkRefNumber: abdm?.clinics?.search?.linkRefNumber,
        otp: action.payload.otp,
        hipId: action.payload.clinicId
      }
    );

    const { data, success } = response;

    if (success === false) {
      setErrorStatus({
        code: "custom",
        message: data?.message
      });
    }

    if (success === true) {
      setSuccessStatus(data?.message);
      getPatientsCareContexts();
      action.payload.navigate?.(
        `/clinicSearch/${action.payload.clinicId}/fetchSuccess`
      );
    }

    yield setAbdmOTPLoading(false);
  } catch (error) {
    yield setAbdmOTPLoading(false);
    yield setErrorStatus(error);
  }
}

function* getScannedClinicDetailsWorker(action) {
  try {
    const { hipId, hipCode, navigate } = action.payload;

    if (typeof hipId !== "string") {
      setErrorStatus("custom", "Hip-Id not available");
    }

    yield setAbdmLoading(true);

    const { patients, auth } = store.getState();
    const { accessToken, currentProfile } = patients;

    const response = yield searchAbdmClinicsWithHipId(
      accessToken?.[currentProfile]?.token,
      auth.data.accessToken,
      currentProfile,
      hipId
    );

    if (response?.success) {
      yield put({
        type: "SET_ABDM_CLINIC_SEARCH_RESULTS",
        payload: {
          searchResults: {
            [response.data.identifier.id]: {
              ...response.data,
              hipCode: hipCode ?? null
            }
          }
        }
      });

      navigate?.(`/clinicSearch/${hipId}`, { replace: true });
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    setErrorStatus(error);
  }
}

function* shareProfileWorker(action) {
  try {
    yield setAbdmLoading(true);

    const { patients, auth, abdm } = store.getState();
    const { accessToken, currentProfile } = patients;
    const { clinicId, currentLocation } = action.payload;

    const clinic = abdm.clinics.search.results?.[clinicId];

    const response = yield shareProfile(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile,
      {
        hipId: clinic?.identifier?.id,
        hipCode: clinic?.hipCode,
        location: currentLocation
      }
    );
    const { data, success } = response;

    if (success === false) {
      setErrorStatus({
        code: "custom",
        message: data?.message
      });
    }

    if (success === true) {
      const searchData = abdm.clinics.search.results;
      yield put({
        type: "SET_ABDM_CLINIC_SEARCH_RESULTS",
        payload: {
          searchResults: {
            ...searchData,
            [clinicId]: {
              ...searchData?.[clinicId],
              token: data.tokenNumber
            }
          }
        }
      });
      setSuccessStatus(data?.message);
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* clearABdmConnectDetailsWorker() {
  try {
    yield setAbdmLoading(true);
    yield put({
      type: "SET_ABDM_QR_CODE_DETAILS",
      payload: {
        qrCodeDetails: null
      }
    });
    yield put({
      type: "SET_ABDM_CLINIC_DETAILS",
      payload: {
        clinicDetails: null
      }
    });

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* clearLinkRefNumberWorker() {
  try {
    yield put({
      type: "SET_ABDM_LINK_REF_NUMBER",
      payload: {
        data: null
      }
    });
  } catch (error) {
    yield setErrorStatus(error);
  }
}

function* shareConnectionToClinicWorker(action) {
  try {
    const { abdm, patients } = store.getState();
    const { qrCodeDetails } = abdm.connect || {};
    const { hipId, hipCode } = qrCodeDetails || {};

    if (typeof hipId !== "string" || typeof hipCode !== "string") {
      setErrorStatus("custom", "Hip-code & Hip-id not available");
    }

    yield setAbdmLoading(true);

    const response = yield acceptAndShareConnectionToClinic(
      patients.accessToken?.[patients.currentProfile]?.token,
      hipId,
      hipCode
    );

    const { data, success } = response;

    if (success === false) {
      setErrorStatus({
        code: "custom",
        message: data?.message
      });
    }

    if (success === true) {
      setSuccessStatus(data?.message);

      if (typeof action.payload.go === "function") {
        action.payload.go(-2);
      }

      yield put({
        type: "SET_ABDM_QR_CODE_DETAILS",
        payload: { qrCodeDetails: null }
      });
      yield put({
        type: "SET_ABDM_CLINIC_DETAILS",
        payload: { clinicDetails: null }
      });
    }
    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* setAbdmTransactionIdWorker(action) {
  yield put({
    type: "SET_ABDM_TRANSACTION_ID",
    payload: action.payload.transactionId
  });
}

function* registerNewAbhaNumberWorker(action) {
  try {
    yield setAbdmLoading(true);

    const { type, aadhaarNumber, otp, mobileNumber, clearNewHealthNumberData } =
      action.payload || {};

    const { auth, abdm, patients } = store.getState();
    const accessToken = auth.data.accessToken;
    const transactionId = abdm.transactionId;

    if (type === "sendAadhaarOtp") {
      const response = yield createAbhaNumber(
        accessToken,
        patients.currentProfile,
        { aadhaarNumber: generateEncryptionValue(aadhaarNumber, "ABHA") },
        "aadhaar",
        "sendOtp"
      );
      const { data, success } = response;

      if (success === false) {
        yield setAbdmLoading(true);
        return setErrorStatus({
          code: "custom",
          message: data?.message
        });
      }

      if (success === true) {
        yield put({
          type: "SET_ABDM_TRANSACTION_ID",
          payload: response.data
        });
        setSuccessStatus(data?.message);
      }
    }

    if (type === "verifyAadhaarOtp") {
      const response = yield createAbhaNumber(
        accessToken,
        patients.currentProfile,
        {
          otp: generateEncryptionValue(otp, "ABHA"),
          transactionId: transactionId?.txnId,
          mobileNumber
        },
        "aadhaar",
        "verifyOtp"
      );

      const { data, success } = response;

      if (success === false) {
        setErrorStatus({
          code: "custom",
          message: data?.message
        });
      }

      if (success === true) {
        yield put({
          type: "SET_ABDM_TRANSACTION_ID",
          payload: data.txnId
        });

        yield put({
          type: "SET_AUTH_INFO",
          payload: {
            ...auth.data,
            transactionId: data.txnId,
            // abdmToken: data.token,
            // ABDMRefreshToken: data.tokens.refreshToken,
            customToken: data?.customToken
          }
        });

        yield put({
          type: "SET_NEW_HEALTH_NUMBER_DATA",
          payload: {
            data: {
              ...data,
              ABHAProfile: {
                ...data.ABHAProfile
                // mobileVerified: false
              }
            }
          }
        });
        setSuccessStatus(data?.message);
      }
    }

    if (type === "sendMobileOtp") {
      const txnId = transactionId?.txnId || transactionId;
      const response = yield createAbhaNumber(
        accessToken,
        patients.currentProfile,
        {
          transactionId: txnId,
          mobileNumber: generateEncryptionValue(
            mobileNumber.toString().replace("+91", ""),
            "ABHA"
          )
        },
        "mobile",
        "sendOtp"
      );

      const { data, success } = response;

      if (success === false) {
        setErrorStatus({
          code: "custom",
          message: data?.message
        });
      }

      if (success === true) {
        yield put({
          type: "SET_ABDM_TRANSACTION_ID",
          payload: response.data
        });
        setSuccessStatus(data?.message);
      }
    }

    if (type === "verifyMobileOtp") {
      // Pass the ABHA token stored from the earlier aadhaar/verifyOtp step so
      // the backend can fetch the full profile and create the patient record.
      const abdmToken = abdm.newHealthNumberData?.token;

      const response = yield createAbhaNumber(
        accessToken,
        patients.currentProfile,
        {
          mobileNumber: generateEncryptionValue(
            mobileNumber.toString().replace("+91", ""),
            "ABHA"
          ),
          transactionId: transactionId?.txnId,
          otp: generateEncryptionValue(otp, "ABHA"),
          ...(abdmToken ? { token: abdmToken } : {})
        },
        "mobile",
        "verifyOtp"
      );

      const { data, success } = response;

      if (success === false) {
        setErrorStatus({
          code: "custom",
          message: data?.message
        });
      }

      if (success === true) {
        const abhaNumberData = abdm.newHealthNumberData;

        // The backend now creates the Firebase account and patient profile at
        // this step (when the entered mobile differed from Aadhaar). Mirror the
        // verifyAadhaarOtp dispatch shape so the rest of the flow (address
        // suggest + confirm, login) has the same data available.
        yield put({
          type: "SET_AUTH_INFO",
          payload: {
            ...auth.data,
            transactionId: data.txnId,
            customToken: data?.customToken
          }
        });

        yield put({
          type: "SET_NEW_HEALTH_NUMBER_DATA",
          payload: {
            data: {
              ...abhaNumberData,
              ...data,
              ABHAProfile: {
                ...abhaNumberData?.ABHAProfile,
                ...data?.ABHAProfile,
                userMobile: mobileNumber.toString().replace("+91", ""),
                mobileVerified: true
              }
            }
          }
        });

        yield put({
          type: "SET_ABDM_TRANSACTION_ID",
          payload: null
        });
        setSuccessStatus(data?.message);
      }
    }

    if (clearNewHealthNumberData) {
      yield put({
        type: "SET_NEW_HEALTH_NUMBER_DATA",
        payload: { data: null }
      });
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* resetAbhaNumberWorker() {
  yield put({
    type: "SET_ABDM_TRANSACTION_ID",
    payload: null
  });

  yield put({
    type: "SET_NEW_HEALTH_NUMBER_DATA",
    payload: { data: null }
  });
}

function* getPatientsTokenHistoryWorker() {
  try {
    yield setAbdmLoading(true);
    const { auth, patients } = store.getState();
    const { accessToken, currentProfile } = patients;

    const fireBaseAccessToken = auth.data.accessToken;

    const response = yield getPatientTokenHistory(
      fireBaseAccessToken,
      accessToken?.[currentProfile]?.token,
      patients.currentProfile
    );
    const { data, success } = response;

    if (success === false) {
      yield setAbdmLoading(true);
      return setErrorStatus({
        code: "custom",
        message: data?.message
      });
    }
    if (success === true) {
      yield put({
        type: "SET_TOKEN_HISTORY",
        payload: response.data
      });
      setSuccessStatus(data?.message);
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    yield setErrorStatus(error);
  }
}

function* abhaNumberCreationRegisterWorker(action) {
  try {
    yield setAbdmLoading(true);
    const { auth, patients, abdm } = store.getState();
    const { currentProfile, accessToken } = patients;

    const response = yield ABHANumberCreateABHAAddress(
      auth.data.accessToken,
      accessToken?.[currentProfile]?.token,
      currentProfile,
      action.payload.data
    );

    const { success, data } = response;

    if (success === false) {
      setErrorStatus({
        code: "custom",
        message: data?.message
      });
    }

    if (success === true) {
      setSuccessStatus(data?.message);
      const abhaNumberData = abdm.newHealthNumberData;

      const authData = store.getState().auth.data;

      if (isValidArray(authData.validProfiles)) {
        yield put({
          type: "SET_AUTH_INFO",
          payload: {
            ...authData,
            validProfiles: [
              ...authData.validProfiles,
              {
                abhaAddress: data?.preferredAbhaAddress,
                fullName: data?.name,
                gender: data?.gender,
                abhaNumber: data?.ABHANumber,
                status: data?.status,
                kycStatus: data?.kycVerified ? "VERIFIED" : "PENDING",
                age: getAge(
                  `${data?.dayOfBirth}-${data?.monthOfBirth}-${data?.yearOfBirth}`
                ),
                patientId: data?.patientId
              }
            ]
          }
        });
      }
      yield put({
        type: "SET_NEW_HEALTH_NUMBER_DATA",
        payload: {
          data: {
            ...abhaNumberData,
            ABHAProfile: {
              ...abhaNumberData.ABHAProfile,
              newHealthId: data?.preferredAbhaAddress
            }
          }
        }
      });
    }

    yield setAbdmLoading(false);
  } catch (error) {
    yield setAbdmLoading(false);
    setErrorStatus(error);
  }
}

export default function* abdmWatcher() {
  yield all([
    takeEvery(
      "ABHA_NUMBER_CREATION_REGISTER",
      abhaNumberCreationRegisterWorker
    ),
    takeEvery("SET_SEARCH_HISTORY", setSearchHistoryWorker),
    takeEvery("CLEAR_FETCHED_RECORDS", clearFetchedRecordsWorker),
    takeEvery("CLEAR_CLINIC_SEARCH_RESULTS", clearClinicSearchResultsWorker),
    takeEvery("GET_PUBLIC_HEALTH_PROGRAMS", getPublicHealthProgramsWorker),
    takeEvery("CLEAR_ABDM_AUTH_CREDENTIALS", clearAbdmAuthCredentialsWorker),
    takeEvery("CHECK_HEALTH_ID_EXISTS", checkHealthIdExistsWorker),
    takeEvery("CLEAR_HEALTH_ID_EXISTS_STATUS", clearHealthIdExistsStatusWorker),
    takeEvery("SEARCH_ABDM_CLINIC", searchAbdmClinicWorker),
    takeEvery(
      "DISCOVER_ABDM_CARE_CONTEXT_WITH_CLINIC_ID",
      discoverCareContextWorker
    ),
    takeEvery(
      "SEND_OTP_TO_LINK_ABDM_CARE_CONTEXT",
      sendOtpToLinkAbdmCareContextWorker
    ),
    takeEvery(
      "CONFIRM_OTP_TO_LINK_ABDM_CARE_CONTEXT",
      confirmOtpToLinkAbdmCareContextWorker
    ),
    takeEvery("GET_SCANNED_CLINIC_DETAILS", getScannedClinicDetailsWorker),
    takeEvery("SHARE_PROFILE", shareProfileWorker),
    takeEvery("CLEAR_ABDM_CONNECT_DETAILS", clearABdmConnectDetailsWorker),
    takeEvery("CLEAR_LINK_REF_NUMBER", clearLinkRefNumberWorker),
    takeEvery("SHARE_CONNECTION_TO_CLINIC", shareConnectionToClinicWorker),
    takeEvery("REGISTER_NEW_ABHA_NUMBER", registerNewAbhaNumberWorker),
    takeEvery("RESET_ABHA_NUMBER_CREATION", resetAbhaNumberWorker),
    takeEvery("GET_ABDM_TRANSACTION_ID", setAbdmTransactionIdWorker),
    takeEvery("GET_TOKEN_HISTORY", getPatientsTokenHistoryWorker)
  ]);
}

function* setAbdmHealthIds(healthId) {
  const healthIds = healthId ? [healthId].flat() : null;
  yield put({
    type: "SET_ABDM_HEALTH_ID",
    payload: {
      healthId: healthIds
    }
  });
}

function* setAbdmLoading(loading) {
  if (store.getState().abdm.loading !== loading) {
    yield put({
      type: "SET_ABDM_LOADING",
      payload: {
        loading
      }
    });
  }
}

function* setAbdmOTPLoading(loading) {
  yield put({
    type: "SET_ABDM_OTP_LOADING",
    payload: {
      loading
    }
  });
}

function* setHealthIdExist(healthIdExists) {
  yield put({
    type: "SET_HEALTH_ID_EXISTS_STATUS",
    payload: {
      healthIdExists
    }
  });
}

const getAge = (dateOfBirth) => {
  const [day, month, year] = dateOfBirth.split("-").map(Number);

  const today = new Date();
  const birthDate = new Date(year, month - 1, day);

  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDifference = today.getMonth() - birthDate.getMonth();

  if (
    monthDifference < 0 ||
    (monthDifference === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};
